import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FieldSchema } from '../../core/models/field-schema.model';

@Component({
  selector: 'app-dcf-common-color-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DcfCommonColorPickerComponent), multi: true }],
  template: `
    <div [class]="fieldSchema?.uiStyle?.containerClass" [style.display]="visible ? '' : 'none'">
      <label *ngIf="fieldSchema?.label" class="block text-sm font-medium text-gray-700 mb-1">
        {{ fieldSchema.label }}
        <span *ngIf="isRequired()" class="text-red-500 ml-1">*</span>
      </label>
      <div class="flex items-center space-x-3">
        <input
          type="color"
          class="h-10 w-20 border border-gray-300 rounded cursor-pointer"
          [disabled]="disabled"
          [(ngModel)]="value"
          (ngModelChange)="onValueChange($event)"
          (blur)="onBlur()">
        <input
          type="text"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          [placeholder]="fieldSchema?.placeholder || '#000000'"
          [disabled]="disabled"
          [(ngModel)]="value"
          (ngModelChange)="onValueChange($event)"
          (blur)="onBlur()">
        <div
          class="h-10 w-10 border-2 border-gray-300 rounded"
          [style.background-color]="value || '#ffffff'">
        </div>
      </div>
      <p *ngIf="getErrorMessage()" class="mt-1 text-sm text-red-600">{{ getErrorMessage() }}</p>
      <p *ngIf="fieldSchema?.helpText && !getErrorMessage()" class="mt-1 text-sm text-gray-500">{{ fieldSchema.helpText }}</p>
    </div>
  `
})
export class DcfCommonColorPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() fieldSchema!: FieldSchema;
  @Input() formControl!: FormControl;
  @Output() valueChange = new EventEmitter<string>();

  value: string = '#000000';
  disabled = false;
  visible = true;
  private destroy$ = new Subject<void>();
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    if (this.formControl) {
      this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => this.value = value);
    }
  }

  writeValue(value: any): void { this.value = value || '#000000'; }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
  setVisible(visible: boolean): void { this.visible = visible; }

  onValueChange(value: string): void {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  onBlur(): void {
    this.onTouched();
  }

  isRequired(): boolean {
    return this.fieldSchema?.validators?.some(v => v.key === 'required') ?? false;
  }

  getErrorMessage(): string {
    if (!this.formControl?.errors || !this.formControl?.touched) return '';
    const errorKey = Object.keys(this.formControl.errors)[0];
    return this.fieldSchema?.errorMessages?.[errorKey] || 'Invalid color';
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }
}
